import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { userManagementAPI, roleManagementAPI } from '../../services/api';

const UserCreateModal = ({ onClose, onSave }) => {
  const [availableRoles, setAvailableRoles] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'employee',
    employeeNumber: '',
    jobTitle: '',
    department: '',
    phoneNumber: '',
    notificationSettings: {
      email: true,
      sms: false,
      push: true
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  const departments = [
    'Human Resources',
    'Information Technology',
    'Finance',
    'Marketing',
    'Sales',
    'Operations',
    'Management'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('notifications.')) {
      const notificationType = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        notificationSettings: {
          ...prev.notificationSettings,
          [notificationType]: checked
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Load available roles
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const response = await roleManagementAPI.getAllRoles();
        setAvailableRoles(response.data.roles || []);
      } catch (error) {
        console.error("Error loading roles:", error);
        // Fallback to default roles
        setAvailableRoles([
          { name: "employee", display_name: "Employee" },
          { name: "manager", display_name: "Manager" },
          { name: "hr_manager", display_name: "HR Manager" },
          { name: "admin", display_name: "Administrator" }
        ]);
      }
    };
    loadRoles();
  }, []);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Create the user data object
      const userData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        role: formData.role,
        employee_number: formData.employeeNumber || undefined,
        job_title: formData.jobTitle || undefined,
        department: formData.department || undefined,
        phone: formData.phoneNumber || undefined,
        notification_settings: formData.notificationSettings
      };

      await userManagementAPI.create(userData);
      onSave();
    } catch (error) {
      console.error('Error creating user:', error);
      setError(error.response?.data?.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Create New User</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-6 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
              {error}
            </div>
          )}

          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    className="form-input mt-1"
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    className="form-input mt-1"
                    placeholder="Enter last name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input mt-1"
                    placeholder="Enter email address"
                  />
                </div>
                <div>
                  <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    className="form-input mt-1"
                    placeholder="Enter phone number"
                  />
                </div>
              </div>
            </div>

            {/* Role and Access */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Role and Access</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                    Role *
                  </label>
                  <select
                    id="role"
                    name="role"
                    required
                    value={formData.role}
                    onChange={handleChange}
                    className="form-select mt-1"
                  >
{availableRoles.map((role) => (
                    <option key={role.name} value={role.name}>
{role.display_name || role.name.charAt(0).toUpperCase() + role.name.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="employeeNumber" className="block text-sm font-medium text-gray-700">
                    Employee Number
                  </label>
                  <input
                    type="text"
                    id="employeeNumber"
                    name="employeeNumber"
                    value={formData.employeeNumber}
                    onChange={handleChange}
                    className="form-input mt-1"
                    placeholder="Auto-generated if empty"
                  />
                </div>
              </div>
            </div>

            {/* Job Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Job Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700">
                    Job Title
                  </label>
                  <input
                    type="text"
                    id="jobTitle"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleChange}
                    className="form-input mt-1"
                    placeholder="Enter job title"
                  />
                </div>
                <div>
                  <label htmlFor="department" className="block text-sm font-medium text-gray-700">
                    Department
                  </label>
                  <select
                    id="department"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    className="form-select mt-1"
                  >
                    <option value="">Select Department</option>
                    {departments.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Notification Settings */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Preferences</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="emailNotifications"
                    name="notifications.email"
                    checked={formData.notificationSettings.email}
                    onChange={handleChange}
                    className="form-checkbox h-4 w-4 text-primary-600"
                  />
                  <label htmlFor="emailNotifications" className="ml-2 text-sm text-gray-700">
                    Email notifications
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="smsNotifications"
                    name="notifications.sms"
                    checked={formData.notificationSettings.sms}
                    onChange={handleChange}
                    className="form-checkbox h-4 w-4 text-primary-600"
                  />
                  <label htmlFor="smsNotifications" className="ml-2 text-sm text-gray-700">
                    SMS notifications
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="pushNotifications"
                    name="notifications.push"
                    checked={formData.notificationSettings.push}
                    onChange={handleChange}
                    className="form-checkbox h-4 w-4 text-primary-600"
                  />
                  <label htmlFor="pushNotifications" className="ml-2 text-sm text-gray-700">
                    Push notifications
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            <p className="font-medium">Note:</p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>A password reset email will be sent to the user's email address</li>
              <li>The user will need to set their password before they can log in</li>
              <li>Employee records will be created automatically if employee information is provided</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserCreateModal;